import React, { useState } from 'react';
import { useMutation, useLazyQuery } from '@apollo/client';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { useReembolsosLiga } from '../../../hooks/useReembolsosLiga';
import { PedidoReembolsoCard, ContadoresStatus } from '../../../components/financeiro';
import {
  ALTERAR_STATUS_REEMBOLSO,
  EXECUTAR_REEMBOLSO,
  FINALIZAR_REEMBOLSO,
  AUDITORIAS_PEDIDO_REEMBOLSO,
} from '../../../services/pedidoReembolsoService';
import type { PedidoReembolsoAuditoria } from '../../../types/pedidoReembolso';

// Aba "Reembolsos" do hub financeiro da liga — Task 4 do plano da Phase 4. Recebe `ligaId` por
// prop, chama useReembolsosLiga(ligaId) e renderiza ContadoresStatus (contagens já prontas do
// servidor) + um PedidoReembolsoCard por pedido, isOrganizador=true/isAdmin=false, com os 5
// callbacks ligados às mutations já existentes de pedidoReembolsoService — mesmo padrão de
// pages/atleta/reembolso/PedidosReembolsoPage.tsx (recarrega via refetch após cada mutation, sem
// atualização otimista). Nenhum componente de UI novo é exportado: o diálogo de histórico é
// inline neste mesmo arquivo (corpo da aba), não um novo módulo.
//
// Diálogo de histórico inline — decisão revisada (attempt 2, code review): o repositório já tem
// `pages/atleta/reembolso/AuditoriaReembolsoDialog.tsx`, mais completo (diff de campos, labels,
// formatação de valor/data). O padrão correto seria promovê-lo para `components/financeiro/`,
// como já foi feito com `PedidoReembolsoCard`. Isso foi CONSIDERADO e BLOQUEADO: tanto
// `__tests__/AuditoriaReembolsoDialog.test.tsx` (`import ... from '../AuditoriaReembolsoDialog'`)
// quanto `__tests__/PedidosReembolsoPage.test.tsx` (`jest.mock('../AuditoriaReembolsoDialog', ...)`)
// dependem do caminho atual — mover o componente quebraria os dois, e o Implementer não pode
// editar arquivo de teste (R-X). Por isso o diálogo aqui permanece uma versão mais simples
// (ação/data), deliberada e revisada — não um esquecimento. O que FOI corrigido nesta revisão:
// o diálogo agora lê `loading`/`error` de `useLazyQuery` e nunca falha silenciosamente.

interface AuditoriasPedidoReembolsoData {
  auditoriasPedidoReembolso: PedidoReembolsoAuditoria[];
}

interface AuditoriasPedidoReembolsoVars {
  pedidoReembolsoId: string;
}

export interface ReembolsosLigaTabProps {
  ligaId: string;
}

const ReembolsosLigaTab: React.FC<ReembolsosLigaTabProps> = ({ ligaId }) => {
  const { pedidos, contagens, loading, error, refetch } = useReembolsosLiga(ligaId);

  const [alterarStatus] = useMutation(ALTERAR_STATUS_REEMBOLSO);
  const [executar] = useMutation(EXECUTAR_REEMBOLSO);
  const [finalizar] = useMutation(FINALIZAR_REEMBOLSO);
  const [buscarAuditorias, { data: auditoriaData, loading: historicoLoading, error: historicoError }] = useLazyQuery<
    AuditoriasPedidoReembolsoData,
    AuditoriasPedidoReembolsoVars
  >(AUDITORIAS_PEDIDO_REEMBOLSO);

  const [historicoAberto, setHistoricoAberto] = useState(false);
  const [historicoPedidoId, setHistoricoPedidoId] = useState<string | null>(null);

  const handleAprovar = async (pedidoId: string) => {
    await alterarStatus({ variables: { input: { pedidoId, novoStatus: 'APROVADO' } } });
    refetch();
  };

  const handleRecusar = async (pedidoId: string) => {
    await alterarStatus({ variables: { input: { pedidoId, novoStatus: 'RECUSADO' } } });
    refetch();
  };

  const handleExecutar = async (pedidoId: string) => {
    await executar({ variables: { pedidoId } });
    refetch();
  };

  const handleFinalizar = async (pedidoId: string) => {
    await finalizar({ variables: { pedidoId } });
    refetch();
  };

  const handleHistorico = (pedidoId: string) => {
    setHistoricoPedidoId(pedidoId);
    buscarAuditorias({ variables: { pedidoReembolsoId: pedidoId } });
    setHistoricoAberto(true);
  };

  const handleRetryHistorico = () => {
    if (historicoPedidoId) {
      buscarAuditorias({ variables: { pedidoReembolsoId: historicoPedidoId } });
    }
  };

  if (error) {
    return (
      <Box>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Tentar novamente
            </Button>
          }
        >
          Erro ao carregar reembolsos. Tente novamente.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <ContadoresStatus contagens={contagens} />

      {pedidos.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nenhum pedido de reembolso no período.
        </Typography>
      ) : (
        pedidos.map((pedido) => (
          <PedidoReembolsoCard
            key={pedido.id}
            pedido={pedido}
            isOrganizador
            isAdmin={false}
            canExecute
            executeDisabledReason={null}
            onAprovar={() => handleAprovar(pedido.id)}
            onRecusar={() => handleRecusar(pedido.id)}
            onExecutar={() => handleExecutar(pedido.id)}
            onFinalizar={() => handleFinalizar(pedido.id)}
            onHistorico={() => handleHistorico(pedido.id)}
          />
        ))
      )}

      <Dialog open={historicoAberto} onClose={() => setHistoricoAberto(false)}>
        <DialogTitle>Histórico do pedido</DialogTitle>
        <DialogContent>
          {historicoError ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={handleRetryHistorico}>
                  Tentar novamente
                </Button>
              }
            >
              Erro ao carregar histórico. Tente novamente.
            </Alert>
          ) : historicoLoading ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          ) : (auditoriaData?.auditoriasPedidoReembolso ?? []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Nenhum registro de histórico para este pedido.
            </Typography>
          ) : (
            <List>
              {(auditoriaData?.auditoriasPedidoReembolso ?? []).map((item) => (
                <ListItem key={item.id}>
                  <ListItemText primary={item.acao} secondary={item.realizadoEm ?? undefined} />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ReembolsosLigaTab;
